import { Injectable, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';

@Injectable()
export class ModalService {
  @Output() closeEvent = new EventEmitter();
  @Output() refreshEvent = new EventEmitter();
  @Output() timerEvent = new EventEmitter();

  constructor(router: Router) {
    this.router = router;
    this.defaultSubmitError = `<div class="modal-font-weight">We cannot process your application at this time.
      Please contact us at 855-880-2559.</div>`;
  }

  modalObj = {};

  getModalData() {
    return this.modalObj;
  }

  callCb() {
    this.cb();
  }

  refreshModal(data) {
    this.refreshEvent.emit(data);
  }

  clearData() {
    this.modalObj = {};
  }

  setModalData(value, cb = () => {}) {
    this.modalObj = value;
    this.cb = cb;
    this.router.navigate([{ outlets: { aux: 'modal' } }], {
      queryParamsHandling: 'preserve',
    });
  }

  closeModal(route = null) {
    if (route !== null) {
      const sanitizedRoute = route.replace(/^\//, '');
      this.closeEvent.emit(sanitizedRoute);
    } else {
      this.closeEvent.emit();
    }
  }

  cipAppErrorModal(body = this.defaultSubmitError) {
    this.refreshModal({
      modalType: 'nonClosable',
      fukTopRight: 'false',
      modalClass: 'pending-modal',
      title: 'Sorry something went wrong ;(',
      body,
      buttons: [],
    });
  }

  plaidLoadingModel() {
    this.setModalData({
      modalType: 'loading',
      fukTopRight: 'false',
      modalClass: 'yodlee-modal-loading',
      title: 'We\'re contacting your bank.',
      body: 'This may take several minutes.',
      buttons: [],
    });
  }

  loadingModel() {
    this.setModalData({
      modalType: 'loading',
      fukTopRight: 'false',
      modalClass: 'modal-loading',
      title: 'Loading',
      body: '',
      buttons: [],
    });
  }
  setModalDataHelper({ title, body, action }) {
    this.setModalData({
      modalType: 'closable',
      fukTopRight: 'true',
      fukTopRightID: 'Interstitial_Close',
      allytmln: 'interstitialClose',
      fukTopRightClass: 'modal-top-right-close-btn',
      modalClass: 'leave-modal',
      title,
      body,
      buttons: [
        {
          id: 'Interstitial_Continue',
          allytmln: 'modalInterstitial-continue',
          classes: 'btn leave_btn',
          action,
          text: 'Continue',
        },
        {
          id: 'Interstitial_Cancel',
          allytmln: 'modalInterstitial-cancel',
          classes: 'btn-flat',
          action: 'close',
          text: 'Go Back',
        },
      ],
    });
  }

  interstitial(action) {
    const title = 'This is where we part ways';
    /* eslint-disable max-len */
    const body = `<div class="margin-bottom-footer padding-right-30 footer-modal-text">
      <h2>You're leaving ally.com and heading to a third-party site we don't own or control.</h2>
      <p>FYI: That site has its own privacy policy and level of security. We suggest reviewing those policies and any terms and conditions on that site. Ready to go?</p>
      </div>`;
    this.setModalDataHelper({ title, body, action });
  }

  pdfModal(action) {
    const title = 'Heads Up';
    const body = `<div class="margin-bottom-footer padding-right-30 footer-modal-text">
      <h2>You're about to open a PDF that may have links to third-party sites Ally doesn’t own or control.</h2>
      <p>FYI: Those sites have their own privacy policies and levels of security. We suggest reviewing those policies and any terms and conditions on those sites. Ready to go?</p>
      </div>`;
    this.setModalDataHelper({ title, body, action });
  }

  cancelApplication() {
    this.setModalData({
      modalType: 'non-closable',
      fukTopRight: 'false',
      modalClass: 'leave-modal',
      title: 'Are you sure you want to leave your application?',
      body: `<div class="margin-bottom-5">
        If you leave now, you'll lose any information you may have completed. To go back,
        select Return to Application.</div><div>Keep in mind, we have no minimum deposit requirement
        to open your account, and there are no monthly maintenance fees.
      </div>`,
      buttons: [
        {
          id: 'okay_btn',
          allytmln: 'modalLeaveApplication-leave',
          classes: 'leave_btn',
          action: 'leaveApp',
          text: 'Leave Application',
        },
        {
          id: 'cancel_btn',
          allytmln: 'modalLeaveApplication-return',
          classes: 'btn-secondary',
          action: 'close',
          text: 'Return to Application',
        },
      ],
    });
  }

  difFunding(event) {
    this.setModalData({
      modalType: 'diffFund',
      fukTopRight: 'true',
      fukTopRightClass: 'modal-top-right-close-btn',
      fukTopRightID: 'modalFundingMethodsClose',
      allytmln: 'modalFundingMethods-close',
      modalClass: 'other-funding-modal',
      title: 'Choose Another Funding Option',
      body: '',
      buttons: [],
    });
  }

  matchNoCredentialsFoundModal() {
    this.refreshModal({
      modalType: 'noCredentials',
      fukTopRight: 'false',
      modalClass: 'modal-no-credentials',
      title: 'Do you already have a relationship with us?',
      body: `We already have your information in our system. We can streamline the application process
        for you, but first, you need to create a username and password for Ally Online.`,
      buttons: [
        {
          id: 'ole_btn',
          allytmln: 'modalOLE-enrollAllyOnline',
          classes: 'leave_btn',
          action: 'ole',
          text: 'Enroll in Ally Online',
        },
        {
          id: 'exit_btn',
          allytmln: 'modalOLE-exitApplication',
          classes: 'btn-flat',
          action: 'leaveApp',
          text: 'Exit application',
        },
      ],
    });
  }

  matchFound() {
    this.refreshModal({
      modalType: 'login',
      fukTopRight: 'false',
      modalClass: 'modal-login',
      title:
        'It looks like you are an existing customer. Log in with your Ally credentials to proceed.',
      body: '',
      buttons: [],
    });
  }

  agreementsModel(pdfLink, text) {
    this.setModalData({
      modalType: 'closable',
      fukTopRight: 'true',
      fukTopRightID: 'Modal_Close',
      allytmln: 'agreementsClose',
      fukTopRightClass: 'modal-top-right-close-btn',
      modalClass: 'user-agreements',
      title: 'Printer-friendly version',
      body: `<div><a class="pdf-link" id="modalAgreementsViewPdf" allytmln="modalAgreements-viewPdf"
        href="${pdfLink}" target="_blank">View as PDF</a><div> ${text}`,
      buttons: [],
    });
  }

  ssnModel() {
    this.setModalData({
      modalType: 'ssn',
      fukTopRight: 'false',
      modalClass: 'yodlee-modal-loading',
      title: '',
      body: `A valid Social Security number is required in order to open an account.
        Contact service at 855-880-2559 if you have any questions.`,
      buttons: [],
    });
  }

  itinModal(modalTitle, modalBody, buttons = false) {
    this.setModalData({
      modalType: 'closable',
      fukTopRight: 'true',
      fukTopRightID: 'itinModal_Close',
      fukTopRightClass: 'modal-top-right-close-btn',
      modalClass: 'generic-modal citizenship',
      title: modalTitle,
      body: modalBody,
      buttons: buttons
      ? [{
          id: 'exit_btn',
          allytmln: 'go back',
          classes: 'leave_btn itin_size',
          action: 'close',
          text: 'Go Back',
        }]
      : [],
    });
  }

  beneficiaryConfirm(title, cb) {
    this.setModalData(
      {
        title,
        modalType: 'closable',
        fukTopRight: 'true',
        fukTopRightID: 'deleteBeneficiaryClose',
        allytmln: 'modalDeleteBeneficiary-close',
        fukTopRightClass: 'modal-top-right-close-btn',
        modalClass: 'forgot-username-modal',
        body: '',
        buttons: [
          {
            id: 'okay_btn',
            allytmln: 'modalConfirmDeleteBeneficiary-yes',
            classes: 'delete-btn',
            action: 'deleteBen',
            text: 'Yes, Delete',
            path: '/edit-beneficiary-info',
          },
          {
            id: 'cancel_btn',
            allytmln: 'modalConfirmDeleteBeneficiary-cancel',
            classes: 'btn-flat',
            action: 'close',
            text: 'No, go back',
            path: '/edit-beneficiary-info',
          },
        ],
      },
      cb
    );
  }

  transferTimes() {
    this.setModalData({
      modalType: 'closable',
      fukTopRight: 'true',
      fukTopRightID: 'Options_Close',
      allytmln: 'modalTransferTimelineDetails-close',
      fukTopRightClass: 'modal-top-right-close-btn-footer',
      modalClass: 'modal-transfer',
      title: 'Transfer Timeline Details',
      body: `<div>
          <p>Keep in mind, we don't process on the weekends or federal holidays,
           so transfers will take longer at those times.</p>
          <table class="modal-transfer-table">
            <thead>
              <tr>
                <th scope="col">Transfer Accounts</th>
                <th scope="col">Submit Transfer</th>
                <th scope="col">Transfer Speed</th>
                <th scope="col">Maximum Transfer Limits Per&nbsp;Day</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div class="td-inner">
                    <div class="transfer-icon-wrapper">
                      <div class="transfer-icon ally-bank"></div>
                      <div class="transfer-icon-text hide-mobile">Ally Bank</div>
                    </div>
                    <div class="arrow-wrapper">&#8594;</div>
                    <div class="transfer-icon-wrapper">
                      <div class="transfer-icon ally-invest"></div>
                      <div class="transfer-icon-text hide-mobile">Ally Invest Account</div>
                    </div>
                    <div class="transfer-icon-text hide-desktop">From Ally Bank accounts to Ally Invest accounts</div>
                  </div>
                </td>
                <td class="hide-mobile">
                  <dl>
                    <dt>Monday - Friday</dt>
                    <dd>Between 8:00 am ET and 3:55 pm ET</dd>
                  </dl>
                </td>
                <td>
                  <div class="hide-desktop">
                    <dl>
                      <dt>Submit Transfer</dt>
                      <dd>
                        <dl>
                          <dt>Monday - Friday</dt>
                          <dd>Between 8:00 am ET and 3:55 pm ET</dd>
                        </dl>
                      </dd>
                    </dl>
                  </div>
                  <dl>
                    <dt class="hide-desktop">Transfer Speed</dt>
                    <dd>
                      <dl>
                        <dt>Initial transfer:</dt>
                        <dd>Up to 1 business day</dd>
                      </dl>
                      <dl>
                        <dt>Future transfers:</dt>
                        <dd>1 - 2 minutes</dd>
                      </dl>
                    </dd>
                  </dl>
                  <div class="hide-desktop">
                    <dl>
                      <dt>Maximum Transfer Limits Per Day</dt>
                      <dd>$250,000</dd>
                    </dl>
                  </div>
                </td>
                <td class="hide-mobile">
                  $250,000
                </td>
              </tr>
              <tr>
                <td>
                  <div class="td-inner">
                    <div class="transfer-icon-wrapper">
                      <div class="transfer-icon other"></div>
                      <div class="transfer-icon-text hide-mobile">Other Institutions</div>
                    </div>
                    <div class="arrow-wrapper">&#8594;</div>
                    <div class="transfer-icon-wrapper">
                      <div class="transfer-icon ally-invest"></div>
                      <div class="transfer-icon-text hide-mobile">Ally Invest Account</div>
                    </div>
                    <div class="transfer-icon-text hide-desktop">From Other Institutions to Ally Invest accounts</div>
                  </div>
                </td>
                <td class="hide-mobile">
                  <dl>
                    <dt>Monday - Friday</dt>
                    <dd>Between 8:00 am ET and 2:55 pm ET</dd>
                  </dl>
                </td>
                <td>
                  <div class="hide-desktop">
                    <dl class="no-margin-bottom">
                      <dt>Submit Transfer</dt>
                      <dd>
                        <dl>
                          <dt>Monday - Friday</dt>
                          <dd>Between 8:00 am ET and 2:55 pm ET</dd>
                        </dl>
                      </dd>
                    </dl>
                  </div>
                  <dl>
                    <dt class="hide-desktop">Transfer Speed</dt>
                    <dd>Up to 3 business days</dd>
                  </dl>
                  <div class="hide-desktop">
                    <dl>
                      <dt>Maximum Transfer Limits Per Day</dt>
                      <dd>$150,000</dd>
                    </dl>
                  </div>
                </td>
                <td class="hide-mobile">
                  $150,000
                </td>
              </tr>
            </tbody>
          </table>
        </div>`,
      buttons: [],
    });
  }

  generic(data) {
    this.setModalData({
      modalType: data.modalType || 'generic',
      fukTopRight: 'true',
      fukTopRightID: data.closeBtnId || 'modalCloseBtn',
      modalCloseAllytmln: data.closeBtnId || 'modal-closeBtn',
      fukTopRightClass: 'modal-top-right-close-btn',
      modalClass: data.modalClass || 'generic-modal',
      title: data.title,
      body: data.content,
      allytmln: data.allytmln || null,
      formData: data.formData || null,
    });
  }

  editInfo(data) {
    this.setModalData({
      modalType: data.modalType || 'editPersonalInfo',
      fukTopRight: data.fukTopRight || 'false',
      fukTopRightID: data.closeBtnId || 'modalCloseBtn',
      modalCloseAllytmln: data.closeBtnId || 'modal-closeBtn',
      fukTopRightClass: 'modal-top-right-close-btn',
      modalClass: data.modalClass || 'generic-modal',
      title: data.title,
      body: data.content,
      allytmln: data.allytmln || null,
      formData: data.formData || null,
    });
  }

  fpsl() {
    this.setModalData({
      title: 'Security Income Program',
      modalType: 'closable',
      fukTopRight: 'true',
      fukTopRightID: 'modalHowSipWorksClose',
      modalCloseAllytmln: 'modalHowSipWorks-close',
      fukTopRightClass: 'modal-top-right-close-btn',
      modalClass: 'fpsl-modal generic-modal',
      body: `<h2>Here's how it works</h2>
        <ul class="fpsl-modal-list">
          <li class="logo sharesowned-logo"><p><b>Enroll:</b> Let us know you want to opt into the program, and we'll auto enroll
          you once you qualify.</p></li>
          <li class="logo collateralize-logo"><p><b>Loan:</b> We'll see if you have any securities that could be loaned out to other
          traders and pay interest while they're being borrowed. They're still your shares though, so you can sell
          at any time.</p></li>
          <li class="logo interest-logo"><p><b>Earn:</b> Sit back and earn. We'll place any interest your shares make into
          your account mid-month in addition to the value of any earned dividends.</p></li>
        </ul>
        <h2>Additional details</h2>
        <ul class="additional-details">
          <li class="disc"><p><b>Borrowed shares:</b> Enrollment doesn't guarantee your shares will be borrowed. Typically, securities that
          get borrowed are in high demand or limited supply. When we borrow your security, we provide collateral for the
          loan in the form of the security's cash value to give the trader protection.</p>
          <li class="disc"><p><b>Dividends:</b> You'll receive substitute payments instead of your regular dividend payment for borrowed
          shares. This may be taxed differently, so talk to a tax advisor if you have questions.</p>
          <li class="disc"><p><b>Proxy voting rights:</b> You give up proxy voting rights while your securities are on loan. While this may
          not be a concern to most investors, if you're a larger investor, it may be.</p>
          <li class="disc"><p><b>Revenue sharing:</b> The loan's interest is shared between you, us, and our carrying firm, Apex Clearing
          Corporation. As the program administrator, Apex keeps 40% of the interest, and we split the remaining 60% equally
          with you.</p>
        </ul>`,
      buttons: [
        {
          id: 'gotit_btn',
          allytmln: 'modalHowSipWorks-gotIt',
          classes: 'btn mobile-primary',
          action: 'close',
          text: 'Got it',
          path: '/investment-preferences',
        },
      ],
    });
  }

  tinInfoModal() {
    this.setModalData({
      title: "What's a Taxpayer Identification Number?",
      modalType: 'closable',
      fukTopRight: 'true',
      fukTopRightID: 'tinInfoModalClose',
      modalCloseAllytmln: 'modalTinInfo-close',
      fukTopRightClass: 'modal-top-right-close-btn',
      modalClass: 'generic-modal tin-info-modal',
      body: `<p>For individuals, this is your Social Security Number or Individual Tax Identification Number (ITIN). It should match the first name listed on the account and will be used for tax reporting purposes.</p>`,
      buttons: [],
    });
  }

  learnMoreTrustedContact() {
    this.setModalData({
      title: 'Trusted contacts, beneficiaries and powers of attorney',
      modalType: 'closable',
      fukTopRight: 'true',
      fukTopRightID: 'modalHowTrustedContactIsDifferentClose',
      modalCloseAllytmln: 'modalHowTrustedContactIsDifferent-close',
      fukTopRightClass: 'modal-top-right-close-btn',
      modalClass: 'generic-modal trusted-contact',
      /* eslint-disable max-len  */
      body: `<p>Quick recap: If we can't get a hold of you, we'll reach out to your trusted contact for basic account info or to let them know about any unusual activity in your account.</p>
          <p>This is different from a beneficiary or power of attorney. A beneficiary is someone you name who'll get the money in your account after your death, and a power of attorney can act on your behalf in certain health and financial situations if you can't make decisions on your own.</p>
          <p>Your beneficiary or power of attorney could also be your trusted contact, but that doesn't have to be the case.</p>`,
      buttons: [
        {
          id: 'close_btn',
          allytmln: 'modalHowTrustedContactIsDifferent',
          classes: 'btn mobile-primary',
          action: 'close',
          text: 'Close',
          path: '/trusted-contact',
        },
      ],
    });
  }
}
